import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Flag, Lock, Unlock, ShieldAlert, Save, RefreshCw, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DrugRegistryAdminPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [onlyOverrides, setOnlyOverrides] = useState(false);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // row being edited
  const [validate, setValidate] = useState(null); // validator response

  const token = () => localStorage.getItem('doctor_token') || localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (onlyFlagged) params.set('only_flagged', 'true');
      if (onlyOverrides) params.set('only_overrides', 'true');
      const r = await axios.get(`${API}/api/admin/drug-registry/list?${params}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      alert('Load failed: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [q, onlyFlagged, onlyOverrides]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (row) => { setEdit({ ...row }); setValidate(null); };

  const revalidate = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/drug-registry/revalidate`, edit, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setValidate(r.data);
    } catch (e) { alert('Revalidate failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const saveOverride = async () => {
    try {
      const r = await axios.post(`${API}/api/admin/drug-registry/override`, edit, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (r.data.still_critical && !window.confirm('Validator still flags this as CRITICAL. Save anyway?')) return;
      alert('Saved as manual override — AI cannot overwrite.');
      setEdit(null);
      load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const flagRow = async (row) => {
    const note = window.prompt(`Flag "${row.brand}" — reason?`, row.flag_note || '');
    if (note === null) return;
    await axios.post(`${API}/api/admin/drug-registry/flag`, { brand_key: row.brand_key, note }, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    load();
  };

  const unlockRow = async (row) => {
    if (!window.confirm(`Unlock "${row.brand}"? AI will be allowed to re-classify.`)) return;
    await axios.post(`${API}/api/admin/drug-registry/unlock`, { brand_key: row.brand_key }, {
      headers: { Authorization: `Bearer ${token()}` },
    });
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="drug-registry-page">
      <div className="max-w-[1280px] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Drug Registry</h1>
            <p className="text-sm text-slate-500">Manual override on any Gemini-classified brand · showing {rows.length} of {total}</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50" data-testid="dr-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search brand or molecule…"
              className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="dr-search" />
          </div>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)} /> Flagged</label>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={onlyOverrides} onChange={e => setOnlyOverrides(e.target.checked)} /> Manual overrides</label>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Brand</th>
                <th className="text-left px-3 py-2">Molecule</th>
                <th className="text-left px-3 py-2">Class</th>
                <th className="text-left px-3 py-2">Strength</th>
                <th className="text-left px-3 py-2">Schedule</th>
                <th className="text-left px-3 py-2">Route</th>
                <th className="text-center px-3 py-2">State</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-500">No rows.</td></tr>}
              {rows.map(r => (
                <tr key={r.brand_key} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dr-row-${r.brand_key}`}>
                  <td className="px-3 py-2 font-medium">{r.brand}</td>
                  <td className="px-3 py-2 text-slate-700">{r.molecule || <em className="text-slate-400">—</em>}</td>
                  <td className="px-3 py-2 text-slate-700">{r.drug_class}</td>
                  <td className="px-3 py-2 text-slate-700">{r.default_strength}</td>
                  <td className="px-3 py-2 text-slate-700">{r.schedule}</td>
                  <td className="px-3 py-2 text-slate-700">{r.route || <em className="text-slate-400">unset</em>}</td>
                  <td className="px-3 py-2 text-center">
                    {r.manual_override && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold uppercase mr-1"><Lock className="w-2.5 h-2.5"/>Locked</span>}
                    {r.flagged && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase"><Flag className="w-2.5 h-2.5"/>Flagged</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEditor(r)} className="text-blue-600 hover:text-blue-800 text-xs font-semibold mr-3" data-testid={`dr-edit-${r.brand_key}`}>Edit</button>
                    <button onClick={() => flagRow(r)} className="text-rose-600 hover:text-rose-800 text-xs font-semibold mr-3">Flag</button>
                    {r.manual_override && <button onClick={() => unlockRow(r)} className="text-slate-600 hover:text-slate-800 text-xs font-semibold">Unlock</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-xl max-w-xl w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Edit · {edit.brand}</h2>
              <button onClick={() => setEdit(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[
                ['molecule', 'Molecule(s) — comma-separated'],
                ['drug_class', 'Drug class'],
                ['default_strength', 'Strength (e.g. 250mg + 125mg)'],
                ['schedule', 'Schedule (H1/H/OTC/…)'],
                ['route', 'Route (oral / iv_im_only / topical / inhaled / any)'],
                ['flag_note', 'Flag note (optional)'],
              ].map(([k, label]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
                  <input value={edit[k] || ''} onChange={e => setEdit({ ...edit, [k]: e.target.value })}
                    className="w-full h-9 border border-slate-300 rounded-md px-2.5 text-sm" data-testid={`dr-edit-${k}`} />
                </div>
              ))}
            </div>

            {validate && (
              <div className={`mt-4 p-3 rounded-lg border ${validate.is_safe ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <div className="flex items-center gap-1.5 text-sm font-semibold mb-1">
                  {validate.is_safe ? <ShieldAlert className="w-4 h-4 text-emerald-600" /> : <ShieldAlert className="w-4 h-4 text-rose-600" />}
                  {validate.is_safe ? 'AI review: SAFE' : `AI review: ${validate.flags.filter(f => f.severity === 'critical').length} critical flag(s)`}
                </div>
                <ul className="text-xs text-slate-700 space-y-0.5">
                  {validate.flags.map((f, i) => (
                    <li key={i}><b className={f.severity === 'critical' ? 'text-rose-700' : 'text-amber-700'}>[{f.severity}]</b> {f.reason}</li>
                  ))}
                  {validate.flags.length === 0 && <li className="text-emerald-700">No issues detected — mapping looks consistent.</li>}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={revalidate} className="px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50" data-testid="dr-revalidate">
                <ShieldAlert className="w-4 h-4 inline mr-1" /> Ask AI to re-review
              </button>
              <button onClick={saveOverride} className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 inline-flex items-center gap-1.5" data-testid="dr-save-override">
                <Save className="w-4 h-4" /> Save override <Lock className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
